// Orphaned-bookings page (Step 5e Phase 7c) — built to the signed-off mockup. Lists tasks with
// orphaned_at != null && resolved_at == null, grouped by cause/source. Each card re-bases /
// re-allocates (inline drawer) or cancels, which clears the marker (audited) and removes it from
// the list. Bulk-dismiss resolves stale past-dated orphans (seasonal-gap hygiene).

import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { C, F, card, Btn } from '../components/admin/adminUi';
import AppHeader from '../components/AppHeader';
import * as api from '../lib/admin';

function whenLabel(o: api.OrphanedBooking): string {
  if (!o.block_start_at) return 'unscheduled enquiry (no time set)';
  try {
    return new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: o.dep_timezone }).format(new Date(o.block_start_at));
  } catch { return new Date(o.block_start_at).toISOString().slice(0, 16).replace('T', ' '); }
}
const STATUS: Record<string, { bg: string; fg: string }> = {
  confirmed: { bg: C.greenLight, fg: C.green }, pending: { bg: C.pill, fg: C.slate },
  enquiry: { bg: C.pill, fg: C.textMuted }, quoted: { bg: C.pill, fg: C.textMuted }, cancelled: { bg: C.redLight, fg: C.red },
};

export default function OrphanedBookings() {
  const [orphans, setOrphans] = useState<api.OrphanedBooking[]>([]);
  const [bases, setBases] = useState<api.AdminBase[]>([]);
  const [aircraft, setAircraft] = useState<api.AdminAircraft[]>([]);
  const [assets, setAssets] = useState<api.AdminAsset[]>([]);
  const [staff, setStaff] = useState<api.AdminStaff[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.listOrphans().then(setOrphans); }, []);
  useEffect(() => {
    load();
    api.listBases().then(setBases); api.listAircraft().then(setAircraft); api.listAssets().then(setAssets); api.listStaff().then(setStaff);
  }, [load]);

  const groups = useMemo(() => {
    const m = new Map<string, { cause: string; label: string; items: api.OrphanedBooking[] }>();
    for (const o of orphans) {
      const key = `${o.orphaned_cause}|${o.orphaned_source}`;
      const g = m.get(key) ?? { cause: (o.orphaned_cause ?? '').replace(/_/g, ' '), label: o.source_label || '—', items: [] };
      g.items.push(o); m.set(key, g);
    }
    return [...m.values()];
  }, [orphans]);

  const pastCount = orphans.filter((o) => o.block_start_at && new Date(o.block_start_at).getTime() < Date.now()).length;

  async function bulkDismissPast() {
    if (!confirm(`Dismiss ${pastCount} past-dated orphan(s)? Each is cancelled and cleared (audited).`)) return;
    setBusy(true);
    for (const o of orphans.filter((x) => x.block_start_at && new Date(x.block_start_at).getTime() < Date.now())) {
      await api.resolveOrphan(o.id, { action: 'cancel', cancel_reason: 'Stale past orphan — bulk dismissed' });
    }
    setBusy(false); load();
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: F }}>
      <AppHeader context="Admin ▸ Orphaned bookings">
        <Link to="/admin" style={{ color: '#9fb4c7', fontSize: 13, textDecoration: 'none' }}>← Admin</Link>
      </AppHeader>
      <main style={{ padding: '20px 18px 60px', maxWidth: 1080, margin: '0 auto' }}>
        <div style={{ ...card }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h1 style={{ fontSize: 18, margin: '0 0 4px' }}>Orphaned bookings</h1>
            {pastCount > 0 && <div style={{ marginLeft: 'auto' }}><Btn kind="ghost" disabled={busy} onClick={bulkDismissPast}>Dismiss {pastCount} past orphan(s)</Btn></div>}
          </div>
          <p style={{ color: C.textDim, fontSize: 13, margin: '0 0 14px' }}>Bookings that lost their base, aircraft, asset or staff member through a deactivation or reallocation. Re-base it, re-allocate a resource, or cancel it — resolving clears it from this list (audited).</p>

          {groups.length === 0 ? <div style={{ color: C.textMuted, fontStyle: 'italic', padding: 20, textAlign: 'center' }}>No orphaned bookings 🎉</div> : groups.map((g) => (
            <div key={g.cause + g.label}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0 8px', fontWeight: 700, color: C.navy }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, padding: '2px 8px', borderRadius: 5, background: C.redLight, color: C.red }}>{g.cause}</span>
                {g.label} · {g.items.length} booking(s)
              </div>
              {g.items.map((o) => (
                <OrphanCard key={o.id} o={o} editing={editing === o.id} onEdit={() => setEditing(editing === o.id ? null : o.id)}
                  bases={bases} aircraft={aircraft} assets={assets} staff={staff} onResolved={() => { setEditing(null); load(); }} />
              ))}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

const sel: CSSProperties = { fontFamily: F, fontSize: 12, padding: '5px 7px', border: `1px solid ${C.borderMed}`, borderRadius: 6, width: '100%' };

function OrphanCard({ o, editing, onEdit, bases, aircraft, assets, staff, onResolved }: {
  o: api.OrphanedBooking; editing: boolean; onEdit: () => void;
  bases: api.AdminBase[]; aircraft: api.AdminAircraft[]; assets: api.AdminAsset[]; staff: api.AdminStaff[]; onResolved: () => void;
}) {
  const [baseId, setBaseId] = useState(o.base?.id ?? '');
  const [resource, setResource] = useState(''); // 'ac:REG' | 'as:id'
  const [instructor, setInstructor] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const st = STATUS[o.booking_status] ?? STATUS.pending;

  async function save() {
    setBusy(true); setErr(null);
    const body: api.OrphanResolveBody = { action: 'reallocate' };
    if (baseId) body.base_id = baseId;
    if (resource.startsWith('ac:')) body.aircraft_registration = resource.slice(3);
    else if (resource.startsWith('as:')) body.asset_id = resource.slice(3);
    if (instructor) body.instructor_user_id = instructor;
    const res = await api.resolveOrphan(o.id, body);
    setBusy(false);
    if (res.error) { setErr(res.error); return; }
    onResolved();
  }
  async function cancelBooking() {
    const reason = prompt('Cancel this booking — reason?');
    if (!reason) return;
    setBusy(true); setErr(null);
    const res = await api.resolveOrphan(o.id, { action: 'cancel', cancel_reason: reason });
    setBusy(false);
    if (res.error) { setErr(res.error); return; }
    onResolved();
  }

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 9, padding: '12px 14px', marginBottom: 10, background: C.surfaceAlt }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700 }}>{o.activity_note || 'Booking'}</span>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: st.bg, color: st.fg }}>{o.booking_status}</span>
      </div>
      <div style={{ color: C.textDim, fontSize: 12.5, marginTop: 3 }}>Was: {o.base?.name ?? '—'} · {whenLabel(o)}</div>
      <div style={{ color: C.red, fontSize: 12, marginTop: 4 }}>Lost: {o.lost_label}</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <Btn kind="accent" onClick={onEdit}>Re-base / re-allocate</Btn>
        <Btn kind="ghost" onClick={cancelBooking}>Cancel booking</Btn>
      </div>
      {editing && (
        <div style={{ marginTop: 10, padding: 12, border: `1px dashed ${C.accentBorder}`, borderRadius: 8, background: C.accentLight, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ minWidth: 150 }}><label style={{ fontSize: 11, fontWeight: 600, color: C.textDim, display: 'block', marginBottom: 3 }}>New base</label>
            <select value={baseId} onChange={(e) => setBaseId(e.target.value)} style={sel}><option value="">— keep —</option>{bases.filter((b) => b.is_active).map((b) => <option key={b.id} value={b.id}>{b.name} ({b.short_code})</option>)}</select></div>
          <div style={{ minWidth: 170 }}><label style={{ fontSize: 11, fontWeight: 600, color: C.textDim, display: 'block', marginBottom: 3 }}>Aircraft / asset</label>
            <select value={resource} onChange={(e) => setResource(e.target.value)} style={sel}>
              <option value="">— keep unallocated —</option>
              {aircraft.filter((a) => a.is_active).map((a) => <option key={a.registration} value={`ac:${a.registration}`}>{a.registration}</option>)}
              {assets.filter((a) => a.is_active).map((a) => <option key={a.id} value={`as:${a.id}`}>{a.name}</option>)}
            </select></div>
          <div style={{ minWidth: 150 }}><label style={{ fontSize: 11, fontWeight: 600, color: C.textDim, display: 'block', marginBottom: 3 }}>Instructor</label>
            <select value={instructor} onChange={(e) => setInstructor(e.target.value)} style={sel}><option value="">— keep unallocated —</option>{staff.filter((s) => s.is_active).map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}</select></div>
          <Btn kind="accent" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save & clear orphan'}</Btn>
          <Btn kind="ghost" onClick={onEdit}>Close</Btn>
        </div>
      )}
      {err && <div style={{ color: C.red, fontSize: 12, marginTop: 6 }}>{err}</div>}
    </div>
  );
}
